<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";
import { openChat, sendMessage, fetchProgress, generateDailySummary, addProgressCheckin, updateProgressCheckin, deleteProgressCheckin, addProgressInsight, updateProgressInsight, deleteProgressInsight, addProgressDailySummary, updateProgressDailySummary, deleteProgressDailySummary, addProgressPractice, updateProgressPractice, deleteProgressPractice, saveProgressDraft, deleteProgressDraft, saveProgressFormula, deleteProgressFormula, fetchDesignPendingEdit, updateDesignPendingEdit, logoutInternalTest, type OpenResponse, type ProgressResponse, type ChatAction } from "../services/chat";

interface Message {
  role: "assistant" | "user";
  content: string;
  timestamp: string;
  actions?: ChatAction[] | undefined;
}

const lessonChoiceFallbackActions: ChatAction[] = [
  { label: "我自己去听微课", value: "__lesson_self_listen", tone: "secondary" },
  { label: "你讲给我听", value: "__lesson_tell", tone: "primary" },
];

/**
 * 后端应当始终随微课选择问题返回 actions；这里是显示层的安全网。
 * 只要小葵的同一条消息确实同时提到了微课、自己学习和“小葵讲给你听”，
 * 就必须让用户可以点选，而不能只看到一句文字问题。
 * 空数组表示用户已经点击过，因此不会在旧消息上反复出现按钮。
 */
function visibleActions(message: Message): ChatAction[] {
  if (Array.isArray(message.actions)) return message.actions;
  const isLessonChoiceQuestion = /(微课|这节课)/u.test(message.content)
    && /自己.{0,24}(?:看|听)/u.test(message.content)
    && /(?:我|小葵).{0,16}讲给你听/u.test(message.content);
  return isLessonChoiceQuestion ? lessonChoiceFallbackActions : [];
}

const messages = ref<Message[]>([]);
const inputText = ref("");
const loading = ref(false);
const errorMessage = ref("");
const chatInfo = ref<OpenResponse | null>(null);
const chatContainer = ref<HTMLElement | null>(null);

// ── 进度面板 ──
const showProgress = ref(false);
const progressData = ref<ProgressResponse | null>(null);
const progressLoading = ref(false);

// 折叠状态
const showPractice = ref(false);
const showVersions = ref(false);
const showCheckins = ref(false);
type EditorKind = "seed" | "formula" | "draft" | "practice" | "dailySummary" | null;
const editorKind = ref<EditorKind>(null);
const editorSaving = ref(false);
const seedForm = ref({ id: undefined as number | undefined, day: 1, summary: "" });
const formulaForm = ref<Record<string, string>>({});
const practiceForm = ref({ id: undefined as number | undefined, date: new Date().toISOString().slice(0, 10), status: "done", note: "" });
const dailySummaryForm = ref({ id: undefined as number | undefined, day: 1, summaryDate: new Date().toISOString().slice(0, 10), taskSummary: "", discussionSummary: "" });
const formulaFields: Array<[string, string]> = [["sunflowerField", "葵花田"], ["sunflowerDisc", "葵花盘"], ["sunflowerSeed", "葵花籽"], ["anchorMoment", "锚点"], ["celebration", "庆祝"], ["commitment", "承诺"], ["partnerPlan", "互助伙伴计划"]];
const designEdit = ref<{ mode: "field" | "formula"; field?: string; label?: string; value?: string; formula?: Record<string, string> } | null>(null);
const designEditSaving = ref(false);

async function openProgress(): Promise<void> {
  showProgress.value = true;
  // 学习进度里的微课、打卡和践行记录可能刚刚在聊天中确认。
  // 每次打开都重新读取，不能继续展示上一次打开时的旧快照。
  progressLoading.value = true;
  try {
    progressData.value = await fetchProgress();
  } catch {
    // 保留已加载过的数据，避免临时网络错误时整个面板变空。
  } finally {
    progressLoading.value = false;
  }
}

function closeProgress(): void {
  showProgress.value = false;
}

function showUsageNotice(): void {
  window.alert("小葵是 AI 陪伴教练，不是人类教练或医疗服务。内部测试会保存聊天和已确认学习记录。请不要输入家长联系方式、住址等不必要隐私；如有立即伤害自己或他人的危险，请立刻告诉身边可信任的成年人并联系当地紧急救助。");
}

async function exitTest(): Promise<void> {
  await logoutInternalTest();
  window.location.assign("/");
}

function togglePractice(): void { showPractice.value = !showPractice.value; }
function toggleVersions(): void { showVersions.value = !showVersions.value; }
function toggleCheckins(): void { showCheckins.value = !showCheckins.value; }

async function refreshProgress(): Promise<void> { progressData.value = await fetchProgress(); }
function promptText(label: string, value = ""): string | null { const result = window.prompt(label, value); return result === null ? null : result.trim(); }
async function editCheckin(entry: { id: number; taskType: string; content: string; day: number }): Promise<void> {
  const taskType = promptText("记录名称", entry.taskType); if (taskType === null) return;
  const content = promptText("记录内容", entry.content); if (content === null) return;
  const dayText = promptText("第几天（1-30）", String(entry.day)); if (dayText === null) return;
  await updateProgressCheckin(entry.id, { taskType, content, day: Number(dayText) }); await refreshProgress();
}
async function addCheckin(): Promise<void> { const day = promptText("第几天（1-30）", String(chatInfo.value?.campDay || 1)); if (day === null) return; const taskType = promptText("记录名称"); if (!taskType) return; const content = promptText("记录内容"); if (!content) return; await addProgressCheckin({ day: Number(day), taskType, content }); await refreshProgress(); }
function addDailySummary(): void { dailySummaryForm.value = { id: undefined, day: chatInfo.value?.campDay || 1, summaryDate: new Date().toISOString().slice(0, 10), taskSummary: "", discussionSummary: "" }; editorKind.value = "dailySummary"; }
function editDailySummary(summary: { id: number; day: number; summaryDate: string; taskSummary: string; discussionSummary: string }): void { dailySummaryForm.value = { ...summary }; editorKind.value = "dailySummary"; }
async function requestDailySummary(): Promise<void> {
  if (loading.value) return;
  loading.value = true;
  errorMessage.value = "";
  try {
    const result = await generateDailySummary();
    if (!result.hasContent || !result.summary) { window.alert(result.message || "今天还没有可以整理的内容。"); return; }
    dailySummaryForm.value = { id: undefined, ...result.summary };
    editorKind.value = "dailySummary";
  } catch (error) { errorMessage.value = error instanceof Error ? error.message : "生成今日总结失败"; }
  finally { loading.value = false; }
}
function addSeed(): void { seedForm.value = { id: undefined, day: chatInfo.value?.campDay || 1, summary: "" }; editorKind.value = "seed"; }
function editSeed(seed: { id: number; summary: string; day?: number }): void { seedForm.value = { id: seed.id, day: seed.day || chatInfo.value?.campDay || 1, summary: seed.summary }; editorKind.value = "seed"; }
function openPractice(record?: { id: number; date: string; status: string; note: string }): void { practiceForm.value = record ? { ...record } : { id: undefined, date: new Date().toISOString().slice(0, 10), status: "done", note: "" }; editorKind.value = "practice"; }
function editPractice(record: { id: number; date: string; status: string; note: string }): void { openPractice(record); }
function addPractice(): void { openPractice(); }
function openFormula(kind: "formula" | "draft"): void { const source = kind === "formula" ? (progressData.value?.habitFormula ?? progressData.value?.habitFormulaDraft) : progressData.value?.habitFormulaDraft; formulaForm.value = Object.fromEntries(formulaFields.map(([key]) => [key, source?.[key] || ""])); editorKind.value = kind; }
function editDraft(): void { openFormula("draft"); }
function editFormula(): void { openFormula("formula"); }
function closeEditor(): void { if (!editorSaving.value) editorKind.value = null; }
async function saveEditor(): Promise<void> {
  editorSaving.value = true;
  try {
    if (editorKind.value === "seed") { if (!seedForm.value.summary.trim()) return; if (seedForm.value.id) await updateProgressInsight(seedForm.value.id, { summary: seedForm.value.summary.trim() }); else await addProgressInsight({ day: Number(seedForm.value.day), summary: seedForm.value.summary.trim() }); }
    if (editorKind.value === "practice") { if (!practiceForm.value.note.trim()) return; const body = { date: practiceForm.value.date, status: practiceForm.value.status, note: practiceForm.value.note.trim() }; if (practiceForm.value.id) await updateProgressPractice(practiceForm.value.id, body); else await addProgressPractice(body); }
    if (editorKind.value === "dailySummary") { if (!dailySummaryForm.value.taskSummary.trim() && !dailySummaryForm.value.discussionSummary.trim()) return; const body = { day: Number(dailySummaryForm.value.day), summaryDate: dailySummaryForm.value.summaryDate, taskSummary: dailySummaryForm.value.taskSummary.trim(), discussionSummary: dailySummaryForm.value.discussionSummary.trim() }; if (dailySummaryForm.value.id) await updateProgressDailySummary(dailySummaryForm.value.id, body); else await addProgressDailySummary(body); }
    if (editorKind.value === "draft") await saveProgressDraft(formulaForm.value);
    if (editorKind.value === "formula") { if (!formulaForm.value.sunflowerSeed?.trim()) return; await saveProgressFormula(formulaForm.value); }
    editorKind.value = null; await refreshProgress();
  } finally { editorSaving.value = false; }
}
async function removeRecord(kind: "checkin" | "seed" | "dailySummary" | "practice" | "draft" | "formula", id?: number): Promise<void> { if (!window.confirm("确认删除这条记录吗？删除后无法恢复。")) return; if (kind === "checkin") await deleteProgressCheckin(id!); if (kind === "seed") await deleteProgressInsight(id!); if (kind === "dailySummary") await deleteProgressDailySummary(id!); if (kind === "practice") await deleteProgressPractice(id!); if (kind === "draft") await deleteProgressDraft(); if (kind === "formula") await deleteProgressFormula(); await refreshProgress(); }

onMounted(async () => {
  try {
    const data = await openChat();
    chatInfo.value = data;
    if (data.history?.length) {
      messages.value = data.history.map(item => ({ role: item.role, content: item.content, timestamp: item.timestamp, actions: item.actions }));
      if (data.actions?.length) {
        const lastAssistant = [...messages.value].reverse().find(item => item.role === "assistant");
        if (lastAssistant) lastAssistant.actions = data.actions;
      }
    }
    if (data.openingMessage) messages.value.push({ role: "assistant", content: data.openingMessage, timestamp: new Date().toISOString(), actions: data.actions });
    await scrollToBottom();
  } catch (error) {
    const message = error instanceof Error ? error.message : "连接失败，请稍后再试";
    if (message.includes("内部测试入口")) { window.location.replace("/"); return; }
    errorMessage.value = message;
  }
});

async function sendChatMessage(msgText: string, displayText = msgText): Promise<void> {
  if (!msgText || loading.value) return;

  inputText.value = "";
  messages.value.push({
    role: "user",
    content: displayText,
    timestamp: new Date().toISOString(),
  });
  loading.value = true;
  errorMessage.value = "";
  await scrollToBottom();

  try {
    const result = await sendMessage(msgText);
    messages.value.push({
      role: "assistant",
      content: result.reply,
      timestamp: new Date().toISOString(),
      actions: result.actions,
    });
    if (chatInfo.value) {
      chatInfo.value.campDay = result.campDay;
    }
  } catch (error) {
    const msg = error instanceof Error ? error.message : "发送失败";
    errorMessage.value = msg;
    messages.value.push({
      role: "assistant",
      content: "😅 抱歉，我暂时没法回复。请稍后再试。",
      timestamp: new Date().toISOString(),
    });
  } finally {
    loading.value = false;
    await scrollToBottom();
  }
}

async function handleSend(): Promise<void> {
  await sendChatMessage(inputText.value.trim());
}

async function handleAction(action: ChatAction, index: number): Promise<void> {
  const current = messages.value[index];
  // 用空数组标记已点击；undefined 会触发微课选择的显示兜底而让旧按钮再次出现。
  if (current) current.actions = [];
  if (action.value === "__design_edit" || action.value === "__formula_edit") {
    try { designEdit.value = await fetchDesignPendingEdit(); }
    catch (error) { errorMessage.value = error instanceof Error ? error.message : "暂时无法打开编辑框"; }
    return;
  }
  await sendChatMessage(action.value, action.label);
}

function closeDesignEdit(): void { if (!designEditSaving.value) designEdit.value = null; }
async function saveDesignEdit(): Promise<void> {
  if (!designEdit.value) return;
  const body = designEdit.value.mode === "formula"
    ? { formula: designEdit.value.formula || {} }
    : { value: designEdit.value.value?.trim() || "" };
  if (designEdit.value.mode === "field" && !body.value) return;
  designEditSaving.value = true;
  try {
    const result = await updateDesignPendingEdit(body);
    designEdit.value = null;
    messages.value.push({ role: "assistant", content: result.reply, timestamp: new Date().toISOString(), actions: result.actions });
    await scrollToBottom();
  } catch (error) { errorMessage.value = error instanceof Error ? error.message : "更新失败"; }
  finally { designEditSaving.value = false; }
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    void handleSend();
  }
}

async function scrollToBottom(): Promise<void> {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
}

function fmtDate(day: number): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), 6, 12); // approx camp start, not critical for display
  const d = new Date(start.getTime() + (day - 1) * 86400000);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function formatAssistantMessage(content: string): string {
  const escaped = content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
  return escaped
    .replace(/\*\*([^*\n]{1,160})\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br>");
}
</script>

<template>
  <div class="chat-shell">
    <!-- ═══ 聊天主界面 ═══ -->
    <div class="chat-view">
      <header class="chat-header">
        <div class="chat-header-left">
          <span class="chat-avatar">🌻</span>
          <div>
            <h1 class="chat-title">小葵</h1>
            <p class="chat-subtitle">第 {{ chatInfo?.campDay ?? "?" }} 天 · {{ chatInfo?.theme ?? "加载中" }}</p>
          </div>
        </div>
        <div class="header-actions"><button class="summary-btn" :disabled="loading" @click="requestDailySummary">📝 今日总结</button><button class="progress-btn" @click="openProgress">📊 学习进度</button><button class="notice-btn" @click="showUsageNotice">使用提示</button><button class="exit-btn" @click="exitTest">退出</button></div>
      </header>

      <div ref="chatContainer" class="chat-messages">
        <p v-if="errorMessage" class="chat-error">{{ errorMessage }}</p>
        <div v-for="(msg, index) in messages" :key="index" class="chat-bubble-row" :class="msg.role">
          <div v-if="msg.role === 'assistant'" class="chat-bubble-avatar">🌻</div>
          <div class="chat-bubble" :class="msg.role">
            <div v-if="msg.role === 'assistant'" class="chat-bubble-text" v-html="formatAssistantMessage(msg.content)"></div>
            <div v-else class="chat-bubble-text">{{ msg.content }}</div>
            <div v-if="msg.role === 'assistant' && visibleActions(msg).length" class="chat-actions">
              <button v-for="action in visibleActions(msg)" :key="action.value" class="chat-action" :class="action.tone || 'secondary'" :disabled="loading" @click="handleAction(action, index)">{{ action.label }}</button>
            </div>
          </div>
          <div v-if="msg.role === 'user'" class="chat-bubble-avatar user-avatar">👤</div>
        </div>
        <div v-if="loading" class="chat-bubble-row assistant">
          <div class="chat-bubble-avatar">🌻</div>
          <div class="chat-bubble assistant"><div class="chat-typing"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div></div>
        </div>
      </div>

      <footer class="chat-input-bar">
        <textarea v-model="inputText" class="chat-input" placeholder="跟小葵说点什么..." rows="1" :disabled="loading" @keydown="handleKeydown" />
        <button class="chat-send-btn" :disabled="!inputText.trim() || loading" @click="handleSend">发送</button>
      </footer>
    </div>

    <!-- ═══ 进度面板 ═══ -->
    <div v-if="showProgress" class="progress-overlay" role="dialog" aria-modal="true" aria-label="我的学习进度">
      <div class="overlay-header">
        <h2>📊 我的学习进度</h2>
        <button class="close-btn" @click="closeProgress">✕</button>
      </div>
      <div class="overlay-body" v-if="progressData">
        <span class="phase-tag">{{ progressData.phase.name }} · 第 {{ progressData.campDay }} 天</span>

        <!-- 微习惯配方 -->
        <template v-if="progressData.habitFormula">
          <div class="section-title">🌻 我的微习惯</div>
          <div class="formula-card">
            <div class="formula-title">微习惯配方 · 最新版本 <span class="record-actions"><button @click="editFormula">编辑</button><button class="danger" @click="removeRecord('formula')">删除</button></span></div>
            <div class="formula-row"><span class="f-icon">🌻</span><span>葵花田：<strong>{{ progressData.habitFormula.sunflowerField }}</strong></span></div>
            <div class="formula-row"><span class="f-icon">🌼</span><span>葵花盘：<strong>{{ progressData.habitFormula.sunflowerDisc }}</strong></span></div>
            <div class="formula-row"><span class="f-icon">🌱</span><span>葵花籽：<strong>{{ progressData.habitFormula.sunflowerSeed }}</strong></span></div>
            <div class="formula-row"><span class="f-icon">⚓</span><span>锚点：<strong>{{ progressData.habitFormula.anchorMoment }}</strong></span></div>
            <div class="formula-row"><span class="f-icon">🎉</span><span>庆祝：<strong>{{ progressData.habitFormula.celebration }}</strong></span></div>
            <div v-if="progressData.habitFormula.commitment" class="formula-row"><span class="f-icon">🤝</span><span>承诺：<strong>{{ progressData.habitFormula.commitment }}</strong></span></div>
            <div v-if="progressData.habitFormula.partnerPlan" class="formula-row"><span class="f-icon">👥</span><span>互助伙伴：<strong>{{ progressData.habitFormula.partnerPlan }}</strong></span></div>

            <!-- 践行记录 -->
            <div class="collapsible practice-collapse">
              <div class="collapsible-header" @click="togglePractice">
                📋 践行记录 <span class="count">{{ progressData.practiceRecords.length }} 条</span>
                <span class="arrow" :class="{ open: showPractice }">▶</span>
              </div>
              <div class="collapsible-body" v-show="showPractice">
                <p v-if="!progressData.practiceRecords.length" class="practice-empty">还没有践行记录。完成一次微动作后，可以在这里记下过程和感受。</p>
                <div class="practice-item" v-for="(p, i) in progressData.practiceRecords" :key="i">
                  <span class="practice-date">{{ p.date }}</span><span>{{ p.note }}</span><span class="record-actions"><button @click="editPractice(p)">编辑</button><button class="danger" @click="removeRecord('practice', p.id)">删除</button></span>
                </div>
                <button class="add-record" @click="addPractice">＋ 添加践行记录</button>
              </div>
            </div>

            <!-- 优化迭代 -->
            <div class="collapsible" v-if="progressData.formulaVersions.length > 1">
              <div class="collapsible-header" @click="toggleVersions">
                🔧 优化迭代 <span class="count">{{ progressData.formulaVersions.length }} 个版本</span>
                <span class="arrow" :class="{ open: showVersions }">▶</span>
              </div>
              <div class="collapsible-body" v-show="showVersions">
                <div class="version-item" v-for="(v, i) in progressData.formulaVersions" :key="i">
                  <div class="version-label">v{{ v.version }} · {{ v.timestamp.slice(0, 10) }}<span v-if="i === progressData.formulaVersions.length - 1" class="current-tag">当前</span></div>
                  {{ v.changed }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-if="progressData.habitFormulaDraft">
          <div class="section-title">📝 微习惯草稿</div>
          <div class="formula-card draft-card">
            <div class="formula-title">进行中的设计 <span class="record-actions"><button @click="editDraft">编辑草稿</button><button class="danger" @click="removeRecord('draft')">删除</button></span></div>
            <div class="formula-row"><span>🌻 葵花田：<strong>{{ progressData.habitFormulaDraft.sunflowerField || '待确认' }}</strong></span></div>
            <div class="formula-row"><span>🌼 葵花盘：<strong>{{ progressData.habitFormulaDraft.sunflowerDisc || '待确认' }}</strong></span></div>
            <div class="formula-row"><span>🌱 葵花籽：<strong>{{ progressData.habitFormulaDraft.sunflowerSeed || '待确认' }}</strong></span></div>
            <div class="formula-row"><span>⚓ 锚点：<strong>{{ progressData.habitFormulaDraft.anchorMoment || '待确认' }}</strong></span></div>
            <div class="formula-row"><span>🎉 庆祝：<strong>{{ progressData.habitFormulaDraft.celebration || '待确认' }}</strong></span></div>
            <div class="formula-row"><span>🤝 承诺：<strong>{{ progressData.habitFormulaDraft.commitment || '待确认' }}</strong></span></div>
            <div class="formula-row"><span>👥 互助伙伴：<strong>{{ progressData.habitFormulaDraft.partnerPlan || '待确认' }}</strong></span></div>
            <button class="add-record" @click="editFormula">确认并转为正式配方</button>
          </div>
        </template>

        <button v-if="!progressData.habitFormula && !progressData.habitFormulaDraft" class="add-record formula-add" @click="editFormula">＋ 添加正式微习惯</button>

        <!-- 学习足迹 -->
        <template v-if="progressData.checkinDays.length">
          <div class="collapsible checkins-collapse" style="margin:8px 16px 0">
            <div class="collapsible-header" @click="toggleCheckins">
              ✨ 学习足迹 <span class="count">{{ progressData.checkinDays.length }} 天有记录</span>
              <span class="arrow" :class="{ open: showCheckins }">▶</span>
            </div>
            <div class="collapsible-body" v-show="showCheckins" style="padding:0">
              <div class="checkin-day" v-for="d in progressData.checkinDays" :key="d.day">
                <div class="checkin-day-header">
                  <span class="checkin-day-num">第 {{ d.day }} 天<span class="date">{{ fmtDate(d.day) }}</span></span>
                </div>
                <div class="checkin-entry" v-for="(e, j) in d.entries" :key="j">
                  <span class="checkin-type" :class="e.isLesson ? 'type-lesson' : (e.isRequired ? 'type-must' : 'type-opt')">{{ e.isLesson ? '微课' : (e.isRequired ? '必选' : '可选') }}</span>
                  <span>{{ e.content }}</span>
                  <span class="record-actions"><button @click="editCheckin({ ...e, day: d.day })">编辑</button><button class="danger" @click="removeRecord('checkin', e.id)">删除</button></span>
                </div>
              </div>
              <button class="add-record" @click="addCheckin">＋ 添加学习记录</button>
            </div>
          </div>
        </template>
        <button v-else class="add-record formula-add" @click="addCheckin">＋ 添加学习记录</button>

        <div class="section-title">📝 每日总结 <button class="small-add" @click="addDailySummary">＋ 添加</button></div>
        <div v-if="progressData.dailySummaries.length" class="daily-summary-card">
          <div v-for="summary in progressData.dailySummaries" :key="summary.id" class="daily-summary-item">
            <div class="daily-summary-title">第 {{ summary.day }} 天总结 <span>{{ summary.summaryDate }}</span><span class="record-actions"><button @click="editDailySummary(summary)">编辑</button><button class="danger" @click="removeRecord('dailySummary', summary.id)">删除</button></span></div>
            <p v-if="summary.taskSummary"><strong>完成内容：</strong>{{ summary.taskSummary }}</p>
            <p v-if="summary.discussionSummary"><strong>重点讨论：</strong>{{ summary.discussionSummary }}</p>
          </div>
        </div>
        <p v-else class="summary-empty">点聊天页顶部的“今日总结”，小葵会先生成草稿，确认后再保存。</p>

        <!-- 行囊 -->
        <template v-if="progressData.insights.length">
          <div class="section-title">🌱 我的行囊 <button class="small-add" @click="addSeed">＋ 添加</button></div>
          <div class="seed-card">
            <div class="seed-item" v-for="(s, i) in progressData.insights" :key="i">
              <span class="seed-icon">🌱</span> {{ s.summary }} · 第{{ s.day }}天 <span class="record-actions"><button @click="editSeed(s)">编辑</button><button class="danger" @click="removeRecord('seed', s.id)">删除</button></span>
            </div>
          </div>
        </template>
        <button v-else class="add-record formula-add" @click="addSeed">＋ 添加智慧种子</button>

        <div v-if="!progressData.habitFormula && !progressData.checkinDays.length && !progressData.insights.length && !progressData.dailySummaries.length" class="empty-state">
          还没有记录～跟小葵聊聊天吧 🌻
        </div>
      </div>
      <div class="overlay-body" v-else-if="progressLoading">
        <div class="empty-state">加载中…</div>
      </div>
    </div>

    <div v-if="designEdit" class="editor-mask" @click.self="closeDesignEdit">
      <form class="editor-dialog" @submit.prevent="saveDesignEdit">
        <div class="editor-header"><h3>{{ designEdit.mode === 'formula' ? '编辑微习惯配方' : `编辑${designEdit.label}` }}</h3><button type="button" class="close-btn" @click="closeDesignEdit">✕</button></div>
        <div v-if="designEdit.mode === 'field'" class="editor-fields">
          <label>{{ designEdit.label }}<textarea v-model="designEdit.value" rows="8" :placeholder="`填写${designEdit.label}`" required /></label>
        </div>
        <div v-else class="editor-fields formula-editor">
          <label v-for="[key, label] in formulaFields" :key="key">{{ label }}<textarea v-model="designEdit.formula![key]" rows="2" :placeholder="`填写${label}`" /></label>
        </div>
        <div class="editor-footer"><button type="button" class="editor-cancel" :disabled="designEditSaving" @click="closeDesignEdit">取消</button><button class="editor-save" :disabled="designEditSaving">{{ designEditSaving ? '更新中…' : '更新为新版本' }}</button></div>
      </form>
    </div>

    <div v-if="editorKind" class="editor-mask" @click.self="closeEditor">
      <form class="editor-dialog" @submit.prevent="saveEditor">
        <div class="editor-header"><h3>{{ editorKind === 'seed' ? (seedForm.id ? '编辑智慧种子' : '添加智慧种子') : editorKind === 'practice' ? (practiceForm.id ? '编辑践行记录' : '添加践行记录') : editorKind === 'dailySummary' ? (dailySummaryForm.id ? '编辑每日总结' : `确认第 ${dailySummaryForm.day} 天总结`) : editorKind === 'draft' ? '编辑微习惯草稿' : '编辑正式微习惯' }}</h3><button type="button" class="close-btn" @click="closeEditor">✕</button></div>
        <div v-if="editorKind === 'seed'" class="editor-fields">
          <label>第几天<input v-model.number="seedForm.day" type="number" min="1" max="30" required /></label>
          <label>智慧种子<textarea v-model="seedForm.summary" rows="5" placeholder="写下你想保留的微习惯洞察" required /></label>
        </div>
        <div v-else-if="editorKind === 'practice'" class="editor-fields">
          <label>日期<input v-model="practiceForm.date" type="date" required /></label>
          <label>践行情况<select v-model="practiceForm.status"><option value="done">完成</option><option value="partial">部分完成</option><option value="skipped">未完成</option></select></label>
          <label>过程与感受<textarea v-model="practiceForm.note" rows="5" placeholder="例如：放学后我完成了检查，感觉更安心" required /></label>
        </div>
        <div v-else-if="editorKind === 'dailySummary'" class="editor-fields">
          <label>课程第几天<input v-model.number="dailySummaryForm.day" type="number" min="1" max="30" required /></label>
          <label>总结日期<input v-model="dailySummaryForm.summaryDate" type="date" required /></label>
          <label>今天完成了什么<textarea v-model="dailySummaryForm.taskSummary" rows="4" placeholder="简要记录已完成的课程任务" /></label>
          <label>重点讨论<textarea v-model="dailySummaryForm.discussionSummary" rows="7" placeholder="如果聊了课程外的困难或问题，在这里保留重点" /></label>
        </div>
        <div v-else class="editor-fields formula-editor">
          <label v-for="[key, label] in formulaFields" :key="key">{{ label }}<textarea v-model="formulaForm[key]" rows="2" :placeholder="`填写${label}`" /></label>
        </div>
        <div class="editor-footer"><button type="button" class="editor-cancel" :disabled="editorSaving" @click="closeEditor">取消</button><button class="editor-save" :disabled="editorSaving">{{ editorSaving ? '保存中…' : editorKind === 'dailySummary' && !dailySummaryForm.id ? '确认保存' : '保存' }}</button></div>
      </form>
    </div>
  </div>
</template>

<style scoped>
/* ── Shell ── */
.chat-shell { position:relative; display:flex; flex-direction:column; height:100dvh; max-width:480px; margin:0 auto; background:#f0efe8 }
.chat-view { flex:1; display:flex; flex-direction:column }

/* ── Header ── */
.chat-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; background:#fff; border-bottom:1px solid #e8e4dc }
.chat-header-left { display:flex; align-items:center; gap:10px }
.header-actions { display:flex;align-items:center;gap:6px }
.chat-avatar { font-size:28px; width:40px;height:40px; display:flex;align-items:center;justify-content:center; background:#fef3e0; border-radius:50% }
.chat-title { font-size:16px; font-weight:700 }
.chat-subtitle { font-size:12px; color:#999 }
.progress-btn { display:flex;align-items:center;gap:4px; padding:6px 12px; border-radius:99px; font-size:12px; font-weight:600; background:#f0a040; color:#fff; border:none; cursor:pointer; white-space:nowrap }
.summary-btn { padding:6px 9px;border-radius:99px;font-size:12px;font-weight:600;background:#fff8ea;color:#9a6615;border:1px solid #f0c57e;cursor:pointer;white-space:nowrap }
.summary-btn:disabled { opacity:.55;cursor:wait }
.notice-btn,.exit-btn{padding:6px 8px;border-radius:99px;font-size:12px;background:#fff;border:1px solid #e6d8bd;color:#6f6250;cursor:pointer;white-space:nowrap}.exit-btn{color:#a24a3a}

/* ── Messages ── */
.chat-messages { flex:1; overflow-y:auto; padding:16px; display:flex; flex-direction:column; gap:14px }
.chat-error { background:#fff0f0; color:#c33; font-size:12px; padding:8px 12px; border-radius:8px; text-align:center }
.chat-bubble-row { display:flex; align-items:flex-end; gap:6px }
.chat-bubble-row.assistant { justify-content:flex-start }
.chat-bubble-row.user { justify-content:flex-end }
.chat-bubble-avatar { font-size:20px; width:30px;height:30px; display:flex;align-items:center;justify-content:center; background:#fef3e0; border-radius:50%; flex-shrink:0 }
.chat-bubble-avatar.user-avatar { background:#f0a040; color:#fff; font-size:14px }
.chat-bubble { max-width:75%; padding:10px 14px; border-radius:16px; font-size:14px; line-height:1.65; white-space:pre-wrap; word-break:break-word }
.chat-bubble.assistant { background:#fff; border:1px solid #e8e4dc; border-bottom-left-radius:4px }
.chat-bubble.user { background:#f0a040; color:#fff; border-bottom-right-radius:4px }
.chat-actions { display:flex; flex-wrap:wrap; gap:8px; margin-top:12px }
.chat-action { border:1px solid #efb76a; border-radius:999px; padding:7px 12px; font-size:13px; cursor:pointer; background:#fff; color:#925813 }
.chat-action.primary { background:#f0a040; border-color:#f0a040; color:#fff; font-weight:600 }
.chat-action:disabled { opacity:.55; cursor:wait }

/* ── Typing ── */
.chat-typing { display:flex; gap:4px; padding:2px 0 }
.chat-typing .dot { width:7px;height:7px; background:#ccc; border-radius:50%; animation:bounce 1.2s infinite }
.chat-typing .dot:nth-child(2) { animation-delay:0.2s }
.chat-typing .dot:nth-child(3) { animation-delay:0.4s }
@keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }

/* ── Input ── */
.chat-input-bar { display:flex; gap:8px; padding:10px 14px; background:#fff; border-top:1px solid #e8e4dc; align-items:flex-end }
.chat-input { flex:1; border:1px solid #e0dcd0; border-radius:20px; padding:10px 16px; font-size:14px; resize:none; outline:none; font-family:inherit; max-height:100px }
.chat-input:focus { border-color:#f0a040 }
.chat-send-btn { background:#f0a040; color:#fff; border:none; border-radius:20px; padding:10px 20px; font-size:14px; font-weight:600; cursor:pointer; flex-shrink:0 }
.chat-send-btn:disabled { background:#ddd; cursor:not-allowed }

/* ── Progress Overlay ── */
/* 学习进度是一个完整页面，不是从右侧滑出的抽屉：打开后覆盖整个聊天界面，
   关闭后只移除覆盖层，原聊天位置和输入内容都保持原样。 */
.progress-overlay { position:fixed; inset:0; z-index:50; display:flex; flex-direction:column; width:100vw; height:100dvh; background:#f0efe8 }
.overlay-header { display:flex;align-items:center;justify-content:space-between; padding:12px 16px; background:#fff; border-bottom:1px solid #e8e4dc }
.overlay-header h2 { font-size:17px }
.close-btn { width:32px;height:32px; border-radius:50%; border:none; background:#e8e4dc; font-size:18px; cursor:pointer; display:flex;align-items:center;justify-content:center }
.overlay-body { flex:1; overflow-y:auto; padding-bottom:30px }
.phase-tag { display:inline-block;background:#f0a040;color:#fff;font-size:11px;font-weight:600;padding:4px 12px;border-radius:99px;margin:16px 16px 0 }
.section-title { font-size:13px;font-weight:700;padding:16px 16px 8px;color:#999 }
.empty-state { text-align:center;padding:60px 20px; color:#999;font-size:14px }

/* ── Formula Card ── */
.formula-card { margin:0 16px 16px; background:linear-gradient(135deg,#fffbf0,#fff5e0); border-radius:14px; padding:16px; border:1px solid #f0d080 }
.formula-title { font-weight:700;font-size:14px;margin-bottom:12px }
.formula-row { display:flex;align-items:flex-start;gap:8px;font-size:13px;margin-bottom:5px;line-height:1.5 }
.draft-card { background:linear-gradient(135deg,#f5fbff,#edf7ff); border-color:#b8d9ed }
.record-actions { margin-left:auto; display:inline-flex; gap:5px; white-space:nowrap; align-items:center }
.record-actions button,.small-add { border:0; background:#fff0d5; color:#9a5b10; border-radius:7px; padding:3px 6px; font-size:11px; cursor:pointer }
.record-actions .danger { background:#fff0f0; color:#ba4c4c }
.add-record { margin:8px 0 0; width:100%; border:1px dashed #d6ad67; background:#fffaf0; color:#9a5b10; border-radius:8px; padding:7px; font-size:12px; cursor:pointer }
.formula-add { margin:0 16px 12px; width:calc(100% - 32px) }
.small-add { float:right; margin-top:-2px }
.f-icon { flex-shrink:0;font-size:15px }
.collapsible { margin-top:10px }
.collapsible-header { display:flex;align-items:center;justify-content:space-between; padding:10px 14px; border-radius:10px; background:#fff; border:1px solid #e8e4dc; cursor:pointer; font-size:13px; font-weight:600 }
.collapsible-header .count { font-size:11px;color:#999;font-weight:400 }
.collapsible-header .arrow { font-size:12px;color:#999;transition:transform .2s }
.collapsible-header .arrow.open { transform:rotate(90deg) }
.collapsible-body { padding:10px 14px; background:#fff; border:1px solid #e8e4dc; border-top:none; border-radius:0 0 10px 10px }
.practice-item { font-size:13px;padding:6px 0;border-bottom:1px dotted #e8e4dc;display:flex;gap:8px;line-height:1.5;align-items:flex-start }
.daily-summary-card { margin:0 16px 10px;background:#fff;border:1px solid #e8e4dc;border-radius:14px;overflow:hidden }
.daily-summary-item { padding:13px 14px;border-bottom:1px solid #f0eadf;font-size:13px;line-height:1.65 }
.daily-summary-item:last-child { border-bottom:none }
.daily-summary-title { display:flex;align-items:center;gap:5px;font-weight:700;color:#604623 }
.daily-summary-title > span:not(.record-actions) { font-size:11px;font-weight:400;color:#999 }
.daily-summary-title .record-actions { margin-left:auto }
.daily-summary-item p { margin:7px 0 0;color:#5d554b }
.summary-empty { margin:0 16px 10px;padding:11px 13px;background:#fffaf1;border-radius:10px;color:#8b7b68;font-size:12px;line-height:1.6 }
.practice-item:last-child { border-bottom:none }
.practice-date { font-size:11px;color:#999;white-space:nowrap;min-width:36px }
.practice-empty { margin:0;padding:8px 0;color:#888;font-size:12px;line-height:1.6 }
.version-item { font-size:13px;padding:8px 0;border-bottom:1px dotted #e8e4dc }
.version-item:last-child { border-bottom:none }

/* ── 进度编辑弹窗 ── */
.editor-mask { position:fixed;inset:0;z-index:30;background:rgba(21,26,32,.42);display:flex;align-items:flex-end;justify-content:center;padding:12px }
.editor-dialog { width:min(480px,100%);max-height:88dvh;overflow:auto;background:#fffdf8;border-radius:18px;box-shadow:0 12px 36px rgba(0,0,0,.2) }
.editor-header { display:flex;align-items:center;justify-content:space-between;padding:16px 18px 12px;border-bottom:1px solid #eee5d8 }
.editor-header h3 { margin:0;font-size:16px;color:#493621 }
.editor-fields { display:flex;flex-direction:column;gap:12px;padding:16px 18px }
.editor-fields label { display:flex;flex-direction:column;gap:6px;font-size:13px;font-weight:600;color:#604623 }
.editor-fields input,.editor-fields textarea,.editor-fields select { width:100%;box-sizing:border-box;border:1px solid #dfd5c5;border-radius:9px;padding:9px 10px;font:inherit;font-size:14px;font-weight:400;color:#333;background:#fff;resize:vertical }
.formula-editor { gap:10px }
.editor-footer { display:flex;gap:10px;justify-content:flex-end;padding:12px 18px 18px;border-top:1px solid #eee5d8 }
.editor-footer button { border:0;border-radius:9px;padding:9px 15px;font-size:14px;cursor:pointer }
.editor-cancel { background:#f2eee7;color:#765d3e }.editor-save { background:#f0a040;color:#fff;font-weight:700 }.editor-footer button:disabled { opacity:.6;cursor:wait }
.version-label { font-size:10px;color:#999;margin-bottom:2px }
.current-tag { background:#fef3e0;color:#b8701e;font-size:10px;padding:1px 6px;border-radius:99px;margin-left:4px }

/* ── Checkins ── */
.checkins-collapse { margin:8px 16px 0 }
.checkin-day { background:#fff; border-radius:14px; padding:14px; margin-bottom:8px; border:1px solid #e8e4dc }
.checkin-day-header { margin-bottom:8px }
.checkin-day-num { font-weight:700;font-size:14px }
.checkin-day-num .date { font-weight:400;font-size:11px;color:#999;margin-left:4px }
.checkin-entry { font-size:13px;padding:5px 0;border-bottom:1px dotted #e8e4dc;display:flex;align-items:flex-start;gap:8px }
.checkin-entry:last-child { border-bottom:none }
.checkin-type { font-size:10px;padding:1px 6px;border-radius:99px;font-weight:600;flex-shrink:0;margin-top:2px }
.type-must { background:#fef3e0;color:#b8701e }
.type-opt { background:#e8f4e8;color:#4a854a }
.type-lesson { background:#e9f0ff;color:#4e6fae }

/* ── Seeds ── */
.seed-card { margin:0 16px 16px; background:linear-gradient(135deg,#f6faf3,#eaf5e7); border-radius:14px; padding:16px; border:1px solid #d4e8cf }
.seed-item { font-size:13px;padding:6px 0;border-bottom:1px dotted #d4e8cf;display:flex;gap:8px;line-height:1.5 }
.seed-item:last-child { border-bottom:none }
.seed-icon { flex-shrink:0 }
</style>
